/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Virtual Dual-Column Bilingual CAT Editor (Task 10.5.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useRef, useState } from 'react';
import {
  Search,
  Filter,
  CheckCircle2,
  Database,
  Layers,
  ArrowRight,
  HelpCircle,
  RotateCcw,
  Zap,
  SlidersHorizontal,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useCatEditorStore, CatStatusFilter } from '../../stores/useCatEditorStore';
import { useVirtualizer } from '../../hooks/useVirtualizer';
import { DualColumnRow } from './DualColumnRow';
import { TmSuggestionDrawer } from './TmSuggestionDrawer';

export const DualColumnEditor: React.FC = () => {
  const { t } = useTranslation();
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [showInspector, setShowInspector] = useState(true);

  const {
    activeSegmentId,
    setActiveSegmentId,
    updateTargetText,
    insertTermIntoActiveSegment,
    commitSegment,
    approveSegment,
    applySuggestion,
    navigateSegment,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    autoJumpOnCommit,
    toggleAutoJump,
    metadata,
    calculateProgress,
    tmLearnedCount,
    resetProject,
    filteredSegments,
  } = useCatEditorStore();

  const segments = filteredSegments();
  const progress = calculateProgress();
  const activeSegment = segments.find((s) => s.id === activeSegmentId) || segments[0];

  // Initialize virtualization hook
  const rowVirtualizer = useVirtualizer({
    count: segments.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 140,
    overscan: 6,
  });

  const filterTabs: Array<{ id: CatStatusFilter; label: string; count?: number }> = [
    { id: 'all', label: t('cat.filterAll') },
    { id: 'untranslated', label: t('cat.filterUntranslated') },
    { id: 'draft', label: t('cat.filterDraft') },
    { id: 'approved', label: t('cat.filterApproved') },
    { id: 'has_warnings', label: t('cat.filterWarnings') },
  ];

  return (
    <div className="flex flex-col h-full bg-surface border border-surface-border rounded-xl shadow-sm overflow-hidden">
      {/* Header & Project Status Bar */}
      <div className="p-4 border-b border-surface-border bg-surface-elevated/40 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Left: Document Info */}
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary border border-primary/20">
              <Layers className="size-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-foreground">
                  {metadata.documentName}
                </h2>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-surface border border-surface-border text-foreground-muted">
                  {metadata.domain}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-muted mt-0.5">
                <span className="font-semibold uppercase text-primary">
                  {metadata.sourceLang}
                </span>
                <ArrowRight className="size-3 text-foreground-muted/60" />
                <span className="font-semibold uppercase text-emerald-500">
                  {metadata.targetLang}
                </span>
                <span className="text-surface-border">|</span>
                <span>
                  {progress.totalSegments} {t('cat.segmentsCountLabel')}
                </span>
              </div>
            </div>
          </div>

          {/* Right: Progress & TM Stats */}
          <div className="flex items-center gap-4">
            {/* TM Ledger Counter */}
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface border border-surface-border text-xs text-foreground-muted"
              title={t('cat.tmLearnedTooltip')}
            >
              <Database className="size-3.5 text-primary" />
              <span>{t('cat.tmLearnedLabel')}:</span>
              <span className="font-semibold text-foreground">{tmLearnedCount}</span>
            </div>

            {/* Auto Jump Toggle */}
            <label className="flex items-center gap-2 cursor-pointer text-xs text-foreground-muted select-none">
              <input
                type="checkbox"
                checked={autoJumpOnCommit}
                onChange={toggleAutoJump}
                className="rounded border-surface-border text-primary focus:ring-primary/20"
              />
              <span>{t('cat.autoJumpLabel')}</span>
            </label>

            {/* Inspector Toggle Button */}
            <button
              type="button"
              onClick={() => setShowInspector(!showInspector)}
              className={`p-1.5 rounded-lg border transition-colors flex items-center gap-1.5 text-xs ${
                showInspector
                  ? 'bg-primary/10 border-primary/30 text-primary'
                  : 'bg-surface border-surface-border text-foreground-muted hover:text-foreground'
              }`}
              title={t('cat.toggleInspector')}
            >
              <SlidersHorizontal className="size-4" />
              <span className="hidden md:inline font-medium">{t('cat.inspectorPanel')}</span>
            </button>

            {/* Shortcuts Guide Button */}
            <button
              type="button"
              onClick={() => setShowShortcutsModal(true)}
              className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface border border-surface-border transition-colors"
              title={t('cat.shortcutsTitle')}
            >
              <HelpCircle className="size-4" />
            </button>
          </div>
        </div>

        {/* Global Progress Bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground-muted flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="size-3.5 text-emerald-500" />
              {t('cat.progressLabel')}
            </span>
            <span className="font-semibold text-foreground">
              {progress.percent}% ({progress.approvedSegments}/{progress.totalSegments})
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-surface-border/40 overflow-hidden flex">
            <div
              className="bg-emerald-500 transition-all duration-300"
              style={{
                width: `${(progress.approvedSegments / Math.max(1, progress.totalSegments)) * 100}%`,
              }}
              title={t('cat.statusApproved')}
            />
            <div
              className="bg-amber-500 transition-all duration-300"
              style={{
                width: `${(progress.draftSegments / Math.max(1, progress.totalSegments)) * 100}%`,
              }}
              title={t('cat.statusDraft')}
            />
          </div>
        </div>
      </div>

      {/* Filter Tabs & Search Bar */}
      <div className="p-3 border-b border-surface-border bg-surface flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Status Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 md:pb-0">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatusFilter(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                statusFilter === tab.id
                  ? 'bg-primary text-primary-fg shadow-sm'
                  : 'text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="relative min-w-[240px]">
          <Search className="size-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('cat.searchPlaceholder')}
            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-foreground-muted hover:text-foreground"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Main Workspace Layout (Grid + Inspector) */}
      <div className="flex-1 flex flex-col xl:flex-row min-h-0 overflow-hidden">
        {/* Virtualized Grid Body */}
        <div
          ref={parentRef}
          className={`flex-1 overflow-y-auto p-4 space-y-4 focus:outline-none transition-all ${
            showInspector ? 'xl:w-8/12 2xl:w-3/4' : 'w-full'
          }`}
          tabIndex={0}
        >
          {segments.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-center p-6 border-2 border-dashed border-surface-border rounded-xl">
              <Filter className="size-8 text-foreground-muted/40 mb-2" />
              <p className="text-sm font-medium text-foreground">
                {t('cat.emptyFilteredTitle')}
              </p>
              <p className="text-xs text-foreground-muted mt-1 max-w-sm">
                {t('cat.emptyFilteredDesc')}
              </p>
              <button
                type="button"
                onClick={() => {
                  setStatusFilter('all');
                  setSearchQuery('');
                }}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-surface-elevated border border-surface-border text-foreground hover:bg-surface"
              >
                <RotateCcw className="size-3.5" />
                {t('cat.resetFilters')}
              </button>
            </div>
          ) : (
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const segment = segments[virtualRow.index];
                if (!segment) return null;

                return (
                  <div
                    key={segment.id}
                    data-index={virtualRow.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                      paddingBottom: '16px',
                    }}
                  >
                    <DualColumnRow
                      segment={segment}
                      isActive={activeSegmentId === segment.id}
                      onActivate={(id) => setActiveSegmentId(id)}
                      onUpdateTarget={(id, text) => updateTargetText(id, text)}
                      onCommit={(id) => commitSegment(id)}
                      onApprove={(id) => approveSegment(id)}
                      onApplySuggestion={(id, type) => applySuggestion(id, type)}
                      onNavigate={(direction) => navigateSegment(direction)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right-Side Real-Time TM / MT / Glossary / QA Inspector Drawer */}
        {showInspector && (
          <div className="xl:w-4/12 2xl:w-1/4 xl:border-l border-t xl:border-t-0 border-surface-border p-4 bg-surface-elevated/20 overflow-y-auto">
            <TmSuggestionDrawer
              segment={activeSegment}
              onApplySuggestion={(id, type) => applySuggestion(id, type)}
              onInsertText={(text) => insertTermIntoActiveSegment(text)}
            />
          </div>
        )}
      </div>

      {/* Footer Shortcut Bar */}
      <div className="px-4 py-2 border-t border-surface-border bg-surface-elevated/60 flex flex-wrap items-center justify-between text-xs text-foreground-muted gap-2">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded bg-surface border border-surface-border font-mono text-[10px] text-foreground">
              Ctrl + Enter
            </kbd>
            <span>{t('cat.shortcutCommit')}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded bg-surface border border-surface-border font-mono text-[10px] text-foreground">
              Ctrl + ↑ / ↓
            </kbd>
            <span>{t('cat.shortcutNavigate')}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded bg-surface border border-surface-border font-mono text-[10px] text-foreground">
              Ctrl + 1
            </kbd>
            <span>{t('cat.shortcutInsertTm')}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded bg-surface border border-surface-border font-mono text-[10px] text-foreground">
              Ctrl + 2
            </kbd>
            <span>{t('cat.shortcutInsertMt')}</span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resetProject}
            className="text-xs text-foreground-muted hover:text-foreground underline decoration-dotted"
          >
            {t('cat.reloadDemoProject')}
          </button>
        </div>
      </div>

      {/* Keyboard Shortcuts Modal */}
      {showShortcutsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-surface border border-surface-border rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="size-5 text-primary" />
                <h3 className="text-base font-semibold text-foreground">
                  {t('cat.shortcutsModalTitle')}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowShortcutsModal(false)}
                className="text-foreground-muted hover:text-foreground text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="flex items-center justify-between py-1.5 border-b border-surface-border">
                <span className="text-foreground-muted">{t('cat.shortcutCommitDesc')}</span>
                <kbd className="px-2 py-1 rounded bg-surface-elevated border border-surface-border font-mono text-foreground">
                  Ctrl + Enter
                </kbd>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-surface-border">
                <span className="text-foreground-muted">{t('cat.shortcutNavigateDesc')}</span>
                <kbd className="px-2 py-1 rounded bg-surface-elevated border border-surface-border font-mono text-foreground">
                  Ctrl + ↑ / ↓
                </kbd>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-surface-border">
                <span className="text-foreground-muted">{t('cat.shortcutInsertTmDesc')}</span>
                <kbd className="px-2 py-1 rounded bg-surface-elevated border border-surface-border font-mono text-foreground">
                  Ctrl + 1
                </kbd>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-surface-border">
                <span className="text-foreground-muted">{t('cat.shortcutInsertMtDesc')}</span>
                <kbd className="px-2 py-1 rounded bg-surface-elevated border border-surface-border font-mono text-foreground">
                  Ctrl + 2
                </kbd>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-surface-border">
                <span className="text-foreground-muted">{t('cat.shortcutInsertTagDesc')}</span>
                <span className="text-foreground font-medium">{t('cat.clickOnTag')}</span>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setShowShortcutsModal(false)}
                className="px-4 py-2 rounded-lg bg-primary text-primary-fg text-xs font-medium hover:bg-primary/90 transition-colors"
              >
                {t('cat.closeModal')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
